import { useEffect, useRef, useState } from 'react'
import { X, Loader2, ShoppingBag, ChevronLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { cartLineOf, useStore, type Store, type CartItem } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'
import { paymentDeclineI18nKey, isRetryable } from '../lib/apiErrorMessage'
import { storePaymentOptions } from '../lib/storePayment'
import { topupBreakdownFor, topupParamsFor } from '../lib/pointsTopup'
import { showToast } from './Toast'
import StripeCheckout from './StripeCheckout'
import AutoTopUpSheet from './AutoTopUpSheet'
import PayMethodToggle from './PayMethodToggle'
import { itemNameLines } from '../lib/itemName'
import { toOrderItems } from '../lib/cartPayload'
import VariationLine from './VariationLine'
import { useCartRejectionRecovery } from '../hooks/useCartRejectionRecovery'
import { uuidv4 } from '../lib/deviceId'

interface Props {
  open: boolean
  store: Store
  items: CartItem[]
  onClose: () => void
  /** #2031 選択肢が変わって弾かれたとき、メニューを取り直す (売り切れ表示を最新にする)。 */
  onMenuStale?: () => void | Promise<void>
  onPaid?: () => void
}

// #2190 verifying = カードは通ったが確定が届かない (やり直している / やり直せなかった)。
// previousOrder = 前のお支払いで注文を受け付けていた (確定が届かずに閉じ、もう一度払おうとした)
type Stage = 'confirm' | 'method' | 'payment' | 'confirming' | 'verifying' | 'previousOrder'

/** #2190 確定のやり直しの間隔 (通信断・5xx のとき。店内・卓の会計シートと同じ) */
const CONFIRM_RETRY_DELAYS_MS = [2000, 4000, 8000]

/**
 * #1213/#1215 カウンター受け取り (takeout) のチェックアウト。
 * 持ち帰り限定ではない — フードコートのように店内で食べる客も同じ導線を使う。
 *
 * DeliveryCheckoutSheet の兄弟だが、住所入力の段が無く「確認 → 決済」の2段:
 *   1. `confirm` — 合計と受け取り方法を確認して「注文して支払う」
 *   2. サーバ: POST /orders/takeout/checkout — 決済 (住所不要・即 capture) と下書きを作る。**注文はまだ作らない**
 *      (#1217)。前のお支払いで注文を受け付けていて画面に出ていなければ 409 previous_order_placed (#2190)
 *   3. `payment` — Stripe Payment Element で払う
 *   4. サーバ: POST /orders/takeout/confirm — 下書きから注文を作る。届かなくても webhook が作る (#2190)。
 *      落ちたら `verifying` で同じ決済の確定をやり直す (払い直させない)
 *   5. カートを空にして /my-orders へ (呼び出し番号と受け取り QR が出る)
 *
 * 先払いなのは客がその場を離れて待つため。受け取りは番号呼び出し (#1215)。
 */
export default function TakeoutCheckoutSheet({ open, store, items, onClose, onPaid, onMenuStale }: Props) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const recoverRejectedCart = useCartRejectionRecovery(items, onMenuStale)
  const { userId, clearCart, wallet, restoreSession } = useStore()

  // #1800 支払手段。ポイントを受ける店では「カード / YOKOSO ポイント」を選べる。
  // 現金のみ運用 (KYC 済み) でポイントを受ける店はポイントだけ (#1769)。
  const { cardInApp: cardAllowed, points: pointsAllowed } = storePaymentOptions(store)
  const [method, setMethod] = useState<'card' | 'points'>(cardAllowed ? 'card' : 'points')
  const [showAutoTopUp, setShowAutoTopUp] = useState(false)
  const balance = wallet?.balanceJPYC ?? 0

  const [notes, setNotes] = useState('')
  // #1171 飲食場所。持ち帰りなら飲食料品が 8%、店内で食べるなら 10%。
  // 既定は「持ち帰り」— カウンター受け取りを選んだ客の多数派で、
  // 店内で食べる客が明示的に切り替える形にする。
  const [diningLocation, setDiningLocation] = useState<'takeout' | 'eat_in'>('takeout')
  const [stage, setStage] = useState<Stage>('confirm')
  const [submitting, setSubmitting] = useState(false)
  const [session, setSession] = useState<{
    clientSecret: string
    paymentIntentId: string
    connectedAccountId: string | null
    totalAmount: number
  } | null>(null)
  // #2190 確定をやり直しても届かなかった / カードの部品が processing を見ている間
  const [verifyFailed, setVerifyFailed] = useState(false)
  const [cardBusy, setCardBusy] = useState(false)
  // #2190 前のお支払いで受け付けていた注文 (呼び出し番号・額)
  const [previousOrder, setPreviousOrder] = useState<{ callNumber?: string; amount: number } | null>(null)
  // #2190 確定のやり直しの途中で画面を離れたら、やり直しをやめて画面を動かさない (webhook が注文にする)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  // #2561 この「支払いの試み」の id。通信が切れて押し直した送り直しを、サーバが同じ決済に畳む。カート・メモ・
  // 飲食場所が変わった・シートを閉じた・払い終えたら振り直す (同じカートをもう一度頼むのは正当なので、試みが
  // 変われば別の決済。QR の #1038 と同じ)
  const attemptIdRef = useRef<string | null>(null)
  useEffect(() => {
    attemptIdRef.current = null
  }, [items, notes, diningLocation, open])

  useEffect(() => {
    if (!open) {
      setStage('confirm')
      setSession(null)
      setShowAutoTopUp(false)
    }
  }, [open])

  if (!open) return null

  const total = items.reduce((n, i) => n + i.price * i.quantity, 0)
  const usePoints = pointsAllowed && (method === 'points' || !cardAllowed)

  // #1805 「注文して支払う」を押したときの行き先。**手段が 1 つしか無い店では
  // 支払い方法の画面を出さない** — 選択肢 1 つの画面を挟むのは操作を増やすだけ。
  const goPay = () => {
    if (cardAllowed && pointsAllowed) {
      setStage('method')
      return
    }
    if (usePoints) startPointsCheckout()
    else void startCheckout()
  }

  // #1800 ポイントで先払い → API が成功した時点で注文が作られている (下書き無し)。
  // 残高不足なら AutoTopUpSheet で不足分のチャージを承認してもらってから呼ぶ。
  const executePointsCheckout = async (topup?: { topupAmount: number; topupFeeJpyc: number }) => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (!items.length || submitting) return
    setSubmitting(true)
    try {
      await api.takeoutPointsCheckout(
        store.id,
        toOrderItems(items),
        notes.trim() || undefined,
        store.reducedTaxEnabled ? diningLocation : undefined,
        topup,
      )
      setStage('confirming')
      clearCart()
      await restoreSession().catch(() => { /* 残高表示だけの問題 */ })
      showToast({ type: 'success', message: t('checkout.thanks') })
      if (onPaid) onPaid()
      else navigate('/my-orders', { replace: true })
    } catch (e) {
      setStage('confirm')
      // #2031 弾かれた行を外してシートを閉じる (メニューで選び直してもらう)
      if (await recoverRejectedCart(e)) { onClose(); return }
      showToast({ type: 'error', message: t(paymentDeclineI18nKey(e) ?? 'checkout.failed') })
    } finally {
      setSubmitting(false)
    }
  }

  const startPointsCheckout = () => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (balance < total) {
      setShowAutoTopUp(true)
      return
    }
    void executePointsCheckout()
  }

  const startCheckout = async () => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (!items.length || submitting) return
    setSubmitting(true)
    try {
      // #1217 ここでは注文を作らない — カートを送って PaymentIntent だけ作る。
      // 決済が通ってから takeoutConfirm で注文化するので、支払いをやめた分は
      // どこにも注文として残らない。
      // 無ければ採番、あれば使い回す (= 同じ試みの送り直しを畳む)
      // crypto.randomUUID の無い端末 (古い iOS・http の場所) でも作れる生成を使う
      attemptIdRef.current ||= uuidv4()
      const checkoutRes = await api.takeoutCheckout(
        store.id,
        toOrderItems(items),
        notes.trim() || undefined,
        // #1171 税率は飲食場所で決まる (受け取り方では決まらない)。
        // 軽減税率対応の店だけ客に聞くので、それ以外は未指定 = 店内飲食 = 10%。
        store.reducedTaxEnabled ? diningLocation : undefined,
        attemptIdRef.current,
      )
      if (!checkoutRes?.clientSecret) {
        showToast({ type: 'error', message: t('checkout.failed') })
        return
      }
      setSession({
        clientSecret: checkoutRes.clientSecret,
        paymentIntentId: checkoutRes.paymentIntentId,
        connectedAccountId: checkoutRes.connectedAccountId || null,
        totalAmount: checkoutRes.totalAmount,
      })
      setStage('payment')
    } catch (e) {
      // #2561 4xx で断られたら、この試みは終わり (次に押したら新しい試み。「新しく注文する」も新しい決済になる)。
      // 5xx・通信断は同じ試みの送り直しなので残す (決まりは domain/payment/order_checkout/ の「試みの終わり方」)
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) attemptIdRef.current = null
      // #2190 前のお支払いで注文を受け付けていた (お客様の画面には出ていない)。払い直させずに知らせる
      if (e instanceof ApiError && e.code === 'previous_order_placed') {
        const detail = (e.detail ?? {}) as { callNumber?: string; amount?: number }
        setPreviousOrder({ callNumber: detail.callNumber, amount: Number(detail.amount ?? 0) })
        setStage('previousOrder')
        return
      }
      if (await recoverRejectedCart(e)) { onClose(); return }
      // #1801 現金のみ運用の店は backend が 409 で断る → 理由をそのまま出す
      showToast({ type: 'error', message: t(paymentDeclineI18nKey(e) ?? 'checkout.failed') })
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * #2190 カードが通った後の確定。落ちても (通信が切れた・混んでいた) カードの部品には戻さず、同じ決済で
   * 確定をやり直す (何度送っても注文は 1 つ)。やり直しても届かなければ「完了している可能性があります」と出して、
   * 払い直させない (サーバでは webhook が注文を作る)。
   */
  const handlePaymentSuccess = async () => {
    if (!session) return
    setVerifyFailed(false)
    setStage('confirming')
    for (let attempt = 0; ; attempt++) {
      try {
        await api.takeoutConfirm(session.paymentIntentId)
        // 支払いが成立したのでこの試みは終わり (振り直さないと「もう 1 杯」が同じ決済に畳まれる)
        attemptIdRef.current = null
        clearCart()
        if (!alive.current) return
        showToast({ type: 'success', message: t('checkout.thanks') })
        if (onPaid) onPaid()
        else navigate('/my-orders', { replace: true })
        return
      } catch (e) {
        console.error('[TakeoutCheckoutSheet] confirm failed:', e)
        // やり直して変わりうるのは通信断・5xx だけ。4xx は何度送っても同じなので、すぐ「確かめられない」に
        if (!isRetryable(e) || attempt >= CONFIRM_RETRY_DELAYS_MS.length) break
        setStage('verifying')
        await new Promise((r) => setTimeout(r, CONFIRM_RETRY_DELAYS_MS[attempt]))
        if (!alive.current) return
      }
    }
    setStage('verifying')
    setVerifyFailed(true)
  }

  // #2190 前のお支払いの注文を見る。カートはその注文のもの (払い直そうとしていた) なので空にする
  const seePreviousOrder = () => {
    clearCart()
    onClose()
    navigate('/my-orders')
  }

  // #2190 確定の途中・カードが処理中の間は閉じさせない (閉じると、何が起きたか分からないまま払い直しうる)
  const closable = !(stage === 'confirming' || (stage === 'verifying' && !verifyFailed)
    || (stage === 'payment' && cardBusy))
  const close = () => { if (closable) onClose() }

  return (
    <>
    <AutoTopUpSheet
      open={showAutoTopUp}
      breakdown={topupBreakdownFor(balance, total)}
      storeName={store.name}
      onCancel={() => setShowAutoTopUp(false)}
      onConfirm={async () => {
        setShowAutoTopUp(false)
        await executePointsCheckout(topupParamsFor(balance, total))
      }}
    />
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={close}
    >
      <div
        className="relative w-full max-w-[430px] bg-white rounded-t-2xl p-6 pb-8 animate-slide-up max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {closable && (
          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        )}

        {stage === 'confirm' && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag size={18} className="text-matcha" />
              <h3 className="font-display font-semibold text-lg text-text-primary">
                {t('takeout.checkoutTitle')}
              </h3>
            </div>
            <p className="font-body text-xs text-text-tertiary mb-4">{store.name}</p>

            <ul className="mb-4 space-y-1.5">
              {items.map((i) => (
                <li key={cartLineOf(i)} className="flex justify-between font-body text-xs">
                  {/* #1857 客の言語が主・日本語が従。カウンターで店員に見せて確認できる */}
                  <span className="min-w-0 flex-1 text-text-secondary">
                    <span className="block">
                      {itemNameLines(i).primary}
                      {i.quantity > 1 && <span className="ml-1 text-text-tertiary">×{i.quantity}</span>}
                    </span>
                    {itemNameLines(i).secondary && (
                      <span className="block font-jp text-[11px] text-text-tertiary">
                        {itemNameLines(i).secondary}
                      </span>
                    )}
                    <VariationLine item={i} />
                  </span>
                  <span className="text-text-primary">¥{(i.price * i.quantity).toLocaleString()}</span>
                </li>
              ))}
            </ul>

            {/* #1171 飲食場所。カウンター受け取りは**店内で食べる客も同じ導線**を
              * 使うので、受け取り方からは税率が決まらない。軽減税率対応の店だけ聞く。
              * 価格は税込のままなので、選んでも支払額は変わらない (レシートの内訳だけ)。 */}
            {store.reducedTaxEnabled && (
              <div className="mb-4">
                <p className="font-body text-xs text-text-secondary mb-1.5">
                  {t('takeout.diningLocationLabel')}
                </p>
                <div className="flex gap-2">
                  {(['takeout', 'eat_in'] as const).map((loc) => (
                    <button
                      key={loc}
                      onClick={() => setDiningLocation(loc)}
                      className={`flex-1 rounded-xl border-2 py-2.5 font-body text-sm font-medium transition-colors ${
                        diningLocation === loc
                          ? 'border-navy bg-navy text-white'
                          : 'border-gray-200 text-text-secondary'
                      }`}
                    >
                      {t(loc === 'takeout' ? 'takeout.diningTakeout' : 'takeout.diningEatIn')}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-xl border border-gray-100 p-3 mb-4 flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">{t('receipt.total')}</span>
              <span className="font-display text-xl font-bold text-text-primary">
                ¥{total.toLocaleString()}
              </span>
            </div>

            <div className="mb-4">
              <label className="block font-body text-xs text-text-secondary mb-1">
                {t('cart.notesLabel')}
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-navy focus:outline-none"
              />
            </div>

            <p className="mb-4 rounded-xl bg-navy/5 border border-navy/10 px-3 py-2 font-body text-[11px] leading-relaxed text-text-secondary">
              {t('takeout.flowHint')}
            </p>

            {/* #1805 支払い方法は「何を注文するか」とは別の質問なので次の画面へ。
              * 同じ画面に置くと、注文内容を確認しながら支払い手段も決めることになる */}
            <button
              onClick={goPay}
              disabled={submitting || !items.length}
              className="tap-effect flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-navy font-display text-base font-semibold text-white disabled:opacity-50"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {t('takeout.payButton')}
            </button>
          </>
        )}

        {/* #1805 支払い方法。**手段が 2 つあるときだけ挟む** — 片方しか使えない店で
          * 選択肢 1 つの画面を出すのは操作を増やすだけ (goPay がそのまま進める) */}
        {stage === 'method' && (
          <>
            <button
              onClick={() => setStage('confirm')}
              className="mb-2 inline-flex items-center gap-1 font-body text-xs text-text-secondary tap-effect"
            >
              <ChevronLeft size={14} />
              {t('takeout.checkoutTitle')}
            </button>
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag size={18} className="text-matcha" />
              <h3 className="font-display font-semibold text-lg text-text-primary">
                {t('checkout.methodTitle')}
              </h3>
            </div>
            <p className="font-body text-xs text-text-tertiary mb-4">
              ¥{total.toLocaleString()} — {store.name}
            </p>

            <PayMethodToggle
              method={usePoints ? 'points' : 'card'}
              onChange={setMethod}
              cardAllowed={cardAllowed}
              balance={balance}
              amount={total}
              showTitle={false}
            />

            <button
              onClick={() => usePoints ? startPointsCheckout() : void startCheckout()}
              disabled={submitting || !items.length}
              className="tap-effect flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-navy font-display text-base font-semibold text-white disabled:opacity-50"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {t('checkout.payNow')}
            </button>
          </>
        )}

        {stage === 'payment' && session && (
          <>
            <h3 className="font-display font-semibold text-lg text-text-primary mb-1">
              {t('takeout.checkoutTitle')}
            </h3>
            <p className="font-body text-xs text-text-tertiary mb-4">
              ¥{session.totalAmount.toLocaleString()} — {store.name}
            </p>
            <StripeCheckout
              clientSecret={session.clientSecret}
              amount={session.totalAmount}
              storeName={store.name}
              storeId={store.id}
              paymentIntentId={session.paymentIntentId}
              connectedAccountId={session.connectedAccountId}
              // 店頭受け取りは即 capture (delivery の manual と違う)
              captureMethod="automatic"
              onSuccess={handlePaymentSuccess}
              onBusyChange={setCardBusy}
              // #1805 カードをやめた人が戻るのは支払い方法の選択 (注文内容の確認ではない)
              onCancel={() => setStage(cardAllowed && pointsAllowed ? 'method' : 'confirm')}
            />
          </>
        )}

        {stage === 'confirming' && (
          <div className="py-10 text-center">
            <Loader2 size={28} className="mx-auto animate-spin text-navy" />
            <p className="mt-3 font-body text-sm text-text-secondary">{t('checkout.confirming')}</p>
          </div>
        )}

        {/* #2190 カードは通ったが確定が届かない。払い直させず、同じ決済で確かめ直す */}
        {stage === 'verifying' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3 text-center" role="status">
            {!verifyFailed && <Loader2 size={28} className="animate-spin text-gold" />}
            <p className="font-display text-base font-semibold text-text-primary">{t('checkout.verifyingTitle')}</p>
            <p className="font-body text-sm text-text-secondary">
              {t(verifyFailed ? 'checkout.verifyFailed' : 'checkout.verifying')}
            </p>
            {verifyFailed && (
              <div className="mt-2 flex w-full flex-col gap-2">
                <button
                  onClick={() => void handlePaymentSuccess()}
                  className="tap-effect h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
                >
                  {t('checkout.verifyAgain')}
                </button>
                <button
                  onClick={() => { onClose(); navigate('/my-orders') }}
                  className="tap-effect h-12 w-full rounded-xl border border-gray-200 font-body text-sm font-medium text-text-secondary"
                >
                  {t('checkout.seeOrders')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* #2190 前のお支払いで注文を受け付けていた (確定が届かずに閉じ、もう一度払おうとした) */}
        {stage === 'previousOrder' && previousOrder && (
          <div className="flex flex-col items-center justify-center py-8 gap-3 text-center" role="status">
            <p className="font-display text-base font-semibold text-text-primary">{t('takeout.previousOrderTitle')}</p>
            <p className="font-body text-sm text-text-secondary">
              {t('takeout.previousOrderBody', {
                number: previousOrder.callNumber ?? '',
                amount: previousOrder.amount.toLocaleString(),
              })}
            </p>
            <div className="mt-2 flex w-full flex-col gap-2">
              <button
                onClick={seePreviousOrder}
                className="tap-effect h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
              >
                {t('checkout.seeOrders')}
              </button>
              <button
                onClick={() => void startCheckout()}
                disabled={submitting}
                className="tap-effect h-12 w-full rounded-xl border border-gray-200 font-body text-sm font-medium text-text-secondary disabled:opacity-50"
              >
                {t('takeout.orderAgain')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
